/**
 * Per-environment API base URL resolution.
 *
 * WU11 (Environment Promotion): the UI now talks to *multiple* backends —
 * one per environment in the promotion ladder. We can't bake a static
 * `REACT_APP_API_URL` into axios.create anymore; we have to compute the
 * baseURL per request from whichever env the user currently has selected.
 *
 * Two pieces here:
 *   1. `getApiBaseUrl(env)` — pure factory that maps an env name to a URL,
 *      reading `REACT_APP_API_URL_<ENV_UPPER>` first, then falling back to
 *      `REACT_APP_API_URL`, then `http://localhost:4000`. Tests can stub by
 *      mutating `process.env`.
 *   2. `setCurrentEnvForApi` / `getCurrentEnvForApi` — a mutable module-level
 *      ref the EnvContext writes to. The axios request interceptor reads it
 *      synchronously on every request. We deliberately keep this OUT of
 *      React state because axios interceptors are plain functions outside the
 *      React tree.
 *
 * The initial value mirrors EnvContext's default (`REACT_APP_EDITABLE_ENV`
 * or `'dev01'`) so any requests fired before EnvProvider mounts (e.g. during
 * Auth0 bootstrapping) still hit the right backend.
 */

/** Build the API base URL for a given env name. */
export const getApiBaseUrl = (env: string): string => {
  const upper = env.toUpperCase().replace(/[^A-Z0-9]/g, '_');
  const perEnv = process.env[`REACT_APP_API_URL_${upper}`];
  if (perEnv && perEnv.length > 0) return perEnv;
  return process.env.REACT_APP_API_URL ?? 'http://localhost:4000';
};

let currentEnvRef: string =
  process.env.REACT_APP_EDITABLE_ENV ?? 'dev01';

/** Update the env used by the axios request interceptor on subsequent requests. */
export const setCurrentEnvForApi = (env: string): void => {
  currentEnvRef = env;
};

/** Read the current env (used by the axios request interceptor). */
export const getCurrentEnvForApi = (): string => currentEnvRef;
